/**
 * Image Optimization Utilities
 * Handles image compression, resizing, and optimization
 */

import { ENV } from '../config/env';

/**
 * Compress and resize image before upload
 */
export const compressImage = async (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }
            
            // Create new File object
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            
            resolve(compressedFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Image load failed'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('File read failed'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Resize image to exact dimensions (for profile pictures, etc.)
 */
export const resizeToExactDimensions = async (file, width, height, quality = 0.9) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        
        // Calculate crop dimensions
        const imgAspect = img.width / img.height;
        const canvasAspect = width / height;
        
        let sourceX, sourceY, sourceWidth, sourceHeight;
        
        if (imgAspect > canvasAspect) {
          // Image is wider than canvas
          sourceHeight = img.height;
          sourceWidth = img.height * canvasAspect;
          sourceX = (img.width - sourceWidth) / 2;
          sourceY = 0;
        } else {
          // Image is taller than canvas
          sourceWidth = img.width;
          sourceHeight = img.width / canvasAspect;
          sourceX = 0;
          sourceY = (img.height - sourceHeight) / 2;
        }
        
        // Draw cropped and resized image
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          width,
          height
        );
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }
            
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            
            resolve(resizedFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Image load failed'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('File read failed'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Get image dimensions
 */
export const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          aspectRatio: img.width / img.height,
        });
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Convert image to WebP format (better compression)
 */
export const convertToWebP = async (file, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas to Blob conversion failed'));
              return;
            }
            
            const fileName = file.name.replace(/\.[^/.]+$/, '.webp');
            const webpFile = new File([blob], fileName, {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            
            resolve(webpFile);
          },
          'image/webp',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Image load failed'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('File read failed'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Generate thumbnail from image
 */
export const generateThumbnail = async (file, size = 150, quality = 0.7) => {
  return resizeToExactDimensions(file, size, size, quality);
};

/**
 * Batch process multiple images
 */
export const batchCompressImages = async (files, options = {}) => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    onProgress = () => {},
  } = options;

  const results = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const compressed = await compressImage(files[i], maxWidth, maxHeight, quality);
      results.push({ success: true, file: compressed, original: files[i] });
      onProgress(i + 1, files.length);
    } catch (error) {
      results.push({ success: false, error, original: files[i] });
      onProgress(i + 1, files.length);
    }
  }
  
  return results;
};

/**
 * Check if file size exceeds limit and compress if needed
 */
export const smartCompress = async (file) => {
  const maxSize = ENV.UPLOAD.MAX_FILE_SIZE;
  
  // If file is already small enough, return as is
  if (file.size <= maxSize) {
    return file;
  }
  
  // Calculate compression needed
  const ratio = maxSize / file.size;
  const quality = Math.max(0.5, Math.min(0.9, ratio));
  
  console.log(`📦 Compressing image (${(file.size / 1024 / 1024).toFixed(2)}MB) with quality ${quality}`);
  
  return await compressImage(file, 1920, 1080, quality);
};

/**
 * Create responsive image set
 */
export const createResponsiveSet = async (file) => {
  const sizes = [
    { name: 'thumbnail', width: 150, height: 150 },
    { name: 'small', width: 480, height: 480 },
    { name: 'medium', width: 1024, height: 1024 },
    { name: 'large', width: 1920, height: 1920 },
  ];
  
  const images = {};
  
  for (const size of sizes) {
    try {
      const resized = await compressImage(file, size.width, size.height, 0.8);
      images[size.name] = resized;
    } catch (error) {
      console.error(`Failed to create ${size.name} image:`, error);
    }
  }
  
  return images;
};

/**
 * Optimize image for profile picture
 */
export const optimizeForProfile = async (file) => {
  const { width, height } = ENV.UPLOAD.PROFILE_IMAGE_DIMENSIONS;
  return await resizeToExactDimensions(file, width, height, 0.9);
};

/**
 * Optimize image for cover photo
 */
export const optimizeForCover = async (file) => {
  const { width, height } = ENV.UPLOAD.COVER_IMAGE_DIMENSIONS;
  return await resizeToExactDimensions(file, width, height, 0.85);
};

/**
 * Get file size in human-readable format
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default {
  compressImage,
  resizeToExactDimensions,
  getImageDimensions,
  convertToWebP,
  generateThumbnail,
  batchCompressImages,
  smartCompress,
  createResponsiveSet,
  optimizeForProfile,
  optimizeForCover,
  formatFileSize,
};

